export type Language = 'ar' | 'fr';

export const SUPPORTED_LANGUAGES: { code: Language; label: string; dir: 'rtl' | 'ltr' }[] = [
  { code: 'ar', label: 'العربية', dir: 'rtl' },
  { code: 'fr', label: 'Français', dir: 'ltr' },
];

export const DEFAULT_LANGUAGE: Language = 'ar';

const translations: Record<Language, Record<string, string>> = {
  ar: {
    // Navigation
    'nav.dashboard': 'لوحة التحكم',
    'nav.catalog': 'كتالوج المنتجات',
    'nav.fifo': 'مخزون FIFO',
    'nav.kanban': 'لوحة الطلبات',
    'nav.marketplace': 'سوق الموردين',
    'nav.order': 'تسجيل طلب',
    'nav.crm': 'إدارة الزبائن',
    'nav.pricelists': 'قوائم الأسعار',
    'nav.promotions': 'الحملات الترويجية',
    'nav.salesreps': 'المندوبون',
    'nav.delivery': 'جدولة التوصيل',
    'nav.fleet': 'إدارة الأسطول',
    'nav.confirmation': 'تأكيد الاستلام',
    'nav.returns': 'إدارة المرتجعات',
    'nav.debt': 'تحصيل الديون',
    'nav.profitability': 'تقارير الربحية',
    'nav.cheques': 'تسوية الشيكات',
    'nav.purchases': 'أوامر الشراء',
    'nav.settings': 'الإعدادات',
    'nav.logout': 'تسجيل الخروج',
    // NEW nav keys
    'nav.kpi': 'لوحة KPI',
    'nav.tva': 'تقرير TVA',
    'nav.roles': 'الأدوار والصلاحيات',
    'nav.barcode': 'طباعة الباركود',
    'nav.whatsapp': 'إشعارات WhatsApp',
    // Sections
    'section.supplier': 'المورد',
    'section.retailer': 'التاجر',
    'section.sales': 'المبيعات والزبائن',
    'section.logistics': 'اللوجيستيك',
    'section.finance': 'المالية',
    'section.purchases': 'المشتريات',
    'section.roles_tools': 'الأدوار والأدوات',
    // Common
    'common.search': 'بحث...',
    'common.add': 'إضافة',
    'common.edit': 'تعديل',
    'common.delete': 'حذف',
    'common.save': 'حفظ',
    'common.cancel': 'إلغاء',
    'common.confirm': 'تأكيد',
    'common.close': 'إغلاق',
    'common.loading': 'جاري التحميل...',
    'common.nodata': 'لا توجد بيانات',
    'common.total': 'المجموع',
    'common.status': 'الحالة',
    'common.date': 'التاريخ',
    'common.amount': 'المبلغ',
    'common.actions': 'الإجراءات',
    'common.filter': 'تصفية',
    'common.export': 'تصدير',
    'common.print': 'طباعة',
    'common.mad': 'درهم',
    'common.zone': 'المنطقة',
    'common.phone': 'الهاتف',
    'common.name': 'الاسم',
    'common.notes': 'ملاحظات',
    'common.active': 'نشط',
    'common.inactive': 'غير نشط',
    'common.pending': 'قيد الانتظار',
    'common.all': 'الكل',
    'common.today': 'اليوم',
    'common.week': 'الأسبوع',
    'common.month': 'الشهر',
    // CRM
    'crm.title': 'إدارة الزبائن (CRM)',
    'crm.subtitle': 'ملفات التجار — تاريخ الطلبات والكريدي ومتوسط الشراء',
    'crm.retailer': 'التاجر',
    'crm.orders': 'الطلبات',
    'crm.credit_limit': 'حد الكريدي',
    'crm.outstanding': 'الرصيد المستحق',
    'crm.avg_purchase': 'متوسط الشراء',
    'crm.last_order': 'آخر طلب',
    'crm.view_profile': 'عرض الملف',
    'crm.order_history': 'تاريخ الطلبات',
    'crm.credit_score': 'تقييم الكريدي',
    // Price Lists
    'pricelists.title': 'قوائم الأسعار',
    'pricelists.subtitle': 'أسعار مخصصة لكل منطقة أو فئة تاجر',
    'pricelists.list_name': 'اسم القائمة',
    'pricelists.zone': 'المنطقة',
    'pricelists.tier': 'الفئة',
    'pricelists.products': 'المنتجات',
    'pricelists.add_list': 'إضافة قائمة أسعار',
    // Promotions
    'promo.title': 'الحملات الترويجية',
    'promo.subtitle': 'عروض موقوتة تُطبَّق تلقائياً على الكتالوج',
    'promo.campaign': 'الحملة',
    'promo.start': 'تاريخ البداية',
    'promo.end': 'تاريخ النهاية',
    'promo.discount': 'الخصم',
    'promo.products': 'المنتجات المشمولة',
    'promo.add': 'إضافة حملة',
    'promo.active': 'نشطة',
    'promo.scheduled': 'مجدولة',
    'promo.expired': 'منتهية',
    // Sales Reps
    'reps.title': 'المندوبون',
    'reps.subtitle': 'محافظ التجار وتتبع العمولات',
    'reps.rep': 'المندوب',
    'reps.portfolio': 'محفظة التجار',
    'reps.commission': 'العمولة',
    'reps.sales': 'المبيعات',
    'reps.add': 'إضافة مندوب',
    'reps.retailers': 'التجار المخصصون',
    // Delivery
    'delivery.title': 'جدولة التوصيل',
    'delivery.subtitle': 'تخصيص الفواتير لسائقين بتواريخ ومسارات',
    'delivery.driver': 'السائق',
    'delivery.route': 'المسار',
    'delivery.invoices': 'الفواتير',
    'delivery.schedule': 'الجدول',
    'delivery.assign': 'تخصيص',
    'delivery.date': 'تاريخ التوصيل',
    // Fleet
    'fleet.title': 'إدارة الأسطول',
    'fleet.subtitle': 'السيارات والسائقون وحمولة اليوم',
    'fleet.vehicle': 'المركبة',
    'fleet.driver': 'السائق',
    'fleet.capacity': 'الطاقة الاستيعابية',
    'fleet.load': 'الحمولة الحالية',
    'fleet.status': 'الحالة',
    'fleet.add_vehicle': 'إضافة مركبة',
    'fleet.add_driver': 'إضافة سائق',
    // Confirmation
    'confirm.title': 'تأكيد الاستلام',
    'confirm.subtitle': 'توقيع رقمي أو صورة من التاجر عند التسليم',
    'confirm.invoice': 'الفاتورة',
    'confirm.retailer': 'التاجر',
    'confirm.driver': 'السائق',
    'confirm.sign': 'توقيع',
    'confirm.photo': 'صورة',
    'confirm.confirmed': 'مؤكد',
    'confirm.pending': 'قيد الانتظار',
    // Returns
    'returns.title': 'إدارة المرتجعات',
    'returns.subtitle': 'إرجاع بضاعة يُعكس تلقائياً على المخزون والفاتورة',
    'returns.invoice': 'الفاتورة الأصلية',
    'returns.product': 'المنتج',
    'returns.quantity': 'الكمية',
    'returns.reason': 'السبب',
    'returns.add': 'تسجيل مرتجع',
    'returns.processed': 'تمت المعالجة',
    // Debt Collection
    'debt.title': 'تحصيل الديون',
    'debt.subtitle': 'جدولة التحصيل وتذكيرات تلقائية عند اقتراب الاستحقاق',
    'debt.retailer': 'التاجر',
    'debt.amount': 'المبلغ',
    'debt.due_date': 'تاريخ الاستحقاق',
    'debt.reminder': 'تذكير',
    'debt.status': 'الحالة',
    'debt.overdue': 'متأخر',
    'debt.due_soon': 'يستحق قريباً',
    'debt.paid': 'مدفوع',
    'debt.schedule': 'جدولة',
    // Profitability
    'profit.title': 'تقارير الربحية',
    'profit.subtitle': 'هامش الربح لكل منتج بناء على تكلفة FIFO مقابل سعر البيع',
    'profit.product': 'المنتج',
    'profit.fifo_cost': 'تكلفة FIFO',
    'profit.sell_price': 'سعر البيع',
    'profit.margin': 'هامش الربح',
    'profit.margin_pct': 'نسبة الهامش',
    'profit.qty_sold': 'الكمية المباعة',
    'profit.revenue': 'الإيراد',
    // Cheques
    'cheques.title': 'تسوية الشيكات',
    'cheques.subtitle': 'تتبع الشيكات المستلمة بتواريخ استحقاقها',
    'cheques.number': 'رقم الشيك',
    'cheques.retailer': 'التاجر',
    'cheques.amount': 'المبلغ',
    'cheques.due_date': 'تاريخ الاستحقاق',
    'cheques.bank': 'البنك',
    'cheques.status': 'الحالة',
    'cheques.pending': 'قيد الانتظار',
    'cheques.deposited': 'مودع',
    'cheques.cleared': 'تم التحصيل',
    'cheques.bounced': 'مرتجع',
    'cheques.add': 'إضافة شيك',
    // Purchase Orders
    'po.title': 'أوامر الشراء',
    'po.subtitle': 'طلبات من مورديكم الخاصين — ربط الدفعات بفواتير الشراء',
    'po.supplier': 'المورد',
    'po.product': 'المنتج',
    'po.quantity': 'الكمية',
    'po.unit_cost': 'تكلفة الوحدة',
    'po.total': 'المجموع',
    'po.status': 'الحالة',
    'po.add': 'إنشاء أمر شراء',
    'po.draft': 'مسودة',
    'po.sent': 'مُرسَل',
    'po.received': 'مستلم',
    'po.linked_batch': 'الدفعة المرتبطة',
    // Dashboard
    'dash.title': 'لوحة التحكم الموحدة',
    'dash.subtitle': 'GerantLink — منصة B2B للموردين والتجار بالمغرب',
    'dash.supplier_view': 'واجهة المورد',
    'dash.retailer_view': 'واجهة التاجر',
    // Topbar
    'topbar.notifications': 'الإشعارات',
    'topbar.new': 'جديد',
    'topbar.see_all': 'عرض الكل',
    'topbar.profile': 'ملفي',
    'topbar.settings': 'الإعدادات',
    'topbar.logout': 'تسجيل الخروج',
    'topbar.zone': 'وسط المدينة، مراكش',
    // Roles
    'role.hybrid': '⬡ هجين',
    'role.supplier': '📦 مورد',
    'role.retailer': '🏪 تاجر',
    // Collapse
    'sidebar.collapse': 'طي القائمة',
  },
  fr: {
    // Navigation
    'nav.dashboard': 'Tableau de Bord',
    'nav.catalog': 'Catalogue Produits',
    'nav.fifo': 'Stock FIFO',
    'nav.kanban': 'Kanban Commandes',
    'nav.marketplace': 'Marketplace',
    'nav.order': 'Passer Commande',
    'nav.crm': 'CRM Clients',
    'nav.pricelists': 'Listes de Prix',
    'nav.promotions': 'Campagnes Promo',
    'nav.salesreps': 'Représentants',
    'nav.delivery': 'Planif. Livraison',
    'nav.fleet': 'Gestion Flotte',
    'nav.confirmation': 'Confirm. Livraison',
    'nav.returns': 'Gestion Retours',
    'nav.debt': 'Recouvrement',
    'nav.profitability': 'Rapports Rentabilité',
    'nav.cheques': 'Règlement Chèques',
    'nav.purchases': 'Bons de Commande',
    'nav.settings': 'Paramètres',
    'nav.logout': 'Déconnexion',
    // NEW nav keys
    'nav.kpi': 'Tableau KPI',
    'nav.tva': 'Rapport TVA',
    'nav.roles': 'Rôles & Permissions',
    'nav.barcode': 'Codes-Barres',
    'nav.whatsapp': 'Notifications WhatsApp',
    // Sections
    'section.supplier': 'Fournisseur',
    'section.retailer': 'Détaillant',
    'section.sales': 'Ventes & Clients',
    'section.logistics': 'Logistique',
    'section.finance': 'Finance',
    'section.purchases': 'Achats',
    'section.roles_tools': 'Rôles & Outils',
    // Common
    'common.search': 'Rechercher...',
    'common.add': 'Ajouter',
    'common.edit': 'Modifier',
    'common.delete': 'Supprimer',
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.confirm': 'Confirmer',
    'common.close': 'Fermer',
    'common.loading': 'Chargement...',
    'common.nodata': 'Aucune donnée',
    'common.total': 'Total',
    'common.status': 'Statut',
    'common.date': 'Date',
    'common.amount': 'Montant',
    'common.actions': 'Actions',
    'common.filter': 'Filtrer',
    'common.export': 'Exporter',
    'common.print': 'Imprimer',
    'common.mad': 'MAD',
    'common.zone': 'Zone',
    'common.phone': 'Téléphone',
    'common.name': 'Nom',
    'common.notes': 'Notes',
    'common.active': 'Actif',
    'common.inactive': 'Inactif',
    'common.pending': 'En attente',
    'common.all': 'Tous',
    'common.today': "Aujourd'hui",
    'common.week': 'Semaine',
    'common.month': 'Mois',
    // CRM
    'crm.title': 'CRM Clients',
    'crm.subtitle': 'Profils détaillants — historique commandes, crédit, achat moyen',
    'crm.retailer': 'Détaillant',
    'crm.orders': 'Commandes',
    'crm.credit_limit': 'Limite Crédit',
    'crm.outstanding': 'Solde Dû',
    'crm.avg_purchase': 'Achat Moyen',
    'crm.last_order': 'Dernière Commande',
    'crm.view_profile': 'Voir Profil',
    'crm.order_history': 'Historique Commandes',
    'crm.credit_score': 'Score Crédit',
    // Price Lists
    'pricelists.title': 'Listes de Prix',
    'pricelists.subtitle': 'Prix personnalisés par zone ou catégorie de détaillant',
    'pricelists.list_name': 'Nom de la liste',
    'pricelists.zone': 'Zone',
    'pricelists.tier': 'Catégorie',
    'pricelists.products': 'Produits',
    'pricelists.add_list': 'Ajouter une liste',
    // Promotions
    'promo.title': 'Campagnes Promotionnelles',
    'promo.subtitle': 'Offres temporisées appliquées automatiquement au catalogue',
    'promo.campaign': 'Campagne',
    'promo.start': 'Date début',
    'promo.end': 'Date fin',
    'promo.discount': 'Remise',
    'promo.products': 'Produits concernés',
    'promo.add': 'Ajouter campagne',
    'promo.active': 'Active',
    'promo.scheduled': 'Planifiée',
    'promo.expired': 'Expirée',
    // Sales Reps
    'reps.title': 'Représentants Commerciaux',
    'reps.subtitle': 'Portefeuilles clients et suivi des commissions',
    'reps.rep': 'Représentant',
    'reps.portfolio': 'Portefeuille',
    'reps.commission': 'Commission',
    'reps.sales': 'Ventes',
    'reps.add': 'Ajouter représentant',
    'reps.retailers': 'Détaillants assignés',
    // Delivery
    'delivery.title': 'Planification Livraisons',
    'delivery.subtitle': 'Assignation des factures aux chauffeurs avec dates et itinéraires',
    'delivery.driver': 'Chauffeur',
    'delivery.route': 'Itinéraire',
    'delivery.invoices': 'Factures',
    'delivery.schedule': 'Planning',
    'delivery.assign': 'Assigner',
    'delivery.date': 'Date livraison',
    // Fleet
    'fleet.title': 'Gestion de la Flotte',
    'fleet.subtitle': 'Véhicules, chauffeurs et charge du jour',
    'fleet.vehicle': 'Véhicule',
    'fleet.driver': 'Chauffeur',
    'fleet.capacity': 'Capacité',
    'fleet.load': 'Charge actuelle',
    'fleet.status': 'Statut',
    'fleet.add_vehicle': 'Ajouter véhicule',
    'fleet.add_driver': 'Ajouter chauffeur',
    // Confirmation
    'confirm.title': 'Confirmation de Livraison',
    'confirm.subtitle': 'Signature numérique ou photo du détaillant à la livraison',
    'confirm.invoice': 'Facture',
    'confirm.retailer': 'Détaillant',
    'confirm.driver': 'Chauffeur',
    'confirm.sign': 'Signer',
    'confirm.photo': 'Photo',
    'confirm.confirmed': 'Confirmé',
    'confirm.pending': 'En attente',
    // Returns
    'returns.title': 'Gestion des Retours',
    'returns.subtitle': 'Retour marchandise avec inversion automatique stock et facture',
    'returns.invoice': 'Facture originale',
    'returns.product': 'Produit',
    'returns.quantity': 'Quantité',
    'returns.reason': 'Motif',
    'returns.add': 'Enregistrer retour',
    'returns.processed': 'Traité',
    // Debt Collection
    'debt.title': 'Recouvrement des Créances',
    'debt.subtitle': 'Planification et rappels automatiques à l\'échéance',
    'debt.retailer': 'Détaillant',
    'debt.amount': 'Montant',
    'debt.due_date': 'Échéance',
    'debt.reminder': 'Rappel',
    'debt.status': 'Statut',
    'debt.overdue': 'En retard',
    'debt.due_soon': 'Bientôt dû',
    'debt.paid': 'Payé',
    'debt.schedule': 'Planifier',
    // Profitability
    'profit.title': 'Rapports de Rentabilité',
    'profit.subtitle': 'Marge par produit — coût FIFO vs prix de vente',
    'profit.product': 'Produit',
    'profit.fifo_cost': 'Coût FIFO',
    'profit.sell_price': 'Prix de vente',
    'profit.margin': 'Marge',
    'profit.margin_pct': '% Marge',
    'profit.qty_sold': 'Qté vendue',
    'profit.revenue': 'Chiffre d\'affaires',
    // Cheques
    'cheques.title': 'Règlement des Chèques',
    'cheques.subtitle': 'Suivi des chèques reçus avec dates d\'échéance',
    'cheques.number': 'N° Chèque',
    'cheques.retailer': 'Détaillant',
    'cheques.amount': 'Montant',
    'cheques.due_date': 'Échéance',
    'cheques.bank': 'Banque',
    'cheques.status': 'Statut',
    'cheques.pending': 'En attente',
    'cheques.deposited': 'Déposé',
    'cheques.cleared': 'Encaissé',
    'cheques.bounced': 'Impayé',
    'cheques.add': 'Ajouter chèque',
    // Purchase Orders
    'po.title': 'Bons de Commande Fournisseurs',
    'po.subtitle': 'Commandes auprès de vos fournisseurs — liaison lots FIFO',
    'po.supplier': 'Fournisseur',
    'po.product': 'Produit',
    'po.quantity': 'Quantité',
    'po.unit_cost': 'Coût unitaire',
    'po.total': 'Total',
    'po.status': 'Statut',
    'po.add': 'Créer bon de commande',
    'po.draft': 'Brouillon',
    'po.sent': 'Envoyé',
    'po.received': 'Reçu',
    'po.linked_batch': 'Lot lié',
    // Dashboard
    'dash.title': 'Tableau de Bord Unifié',
    'dash.subtitle': 'GerantLink — Plateforme B2B Grossiste Maroc',
    'dash.supplier_view': 'Vue Fournisseur',
    'dash.retailer_view': 'Vue Détaillant',
    // Topbar
    'topbar.notifications': 'Notifications',
    'topbar.new': 'nouvelles',
    'topbar.see_all': 'Voir toutes',
    'topbar.profile': 'Mon Profil',
    'topbar.settings': 'Paramètres',
    'topbar.logout': 'Déconnexion',
    'topbar.zone': 'Centre Ville, Marrakech',
    // Roles
    'role.hybrid': '⬡ Hybride',
    'role.supplier': '📦 Fournisseur',
    'role.retailer': '🏪 Détaillant',
    // Collapse
    'sidebar.collapse': 'Réduire',
  },
};

export function getTranslations(language: Language) {
  return (key: string): string => {
    return translations[language]?.[key] ?? translations['fr']?.[key] ?? key;
  };
}

export function getDir(language: Language): 'rtl' | 'ltr' {
  return SUPPORTED_LANGUAGES.find((l) => l.code === language)?.dir ?? 'ltr';
}
